import {
    DndContext,
    type DragEndEvent,
    type DragMoveEvent,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    horizontalListSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import React from 'react';
import { createPortal } from 'react-dom';

import { VerticalAlignment } from '@gitbook/api-client';
import {
    type TableColumnDefinition,
    type TableDefinitions,
    type TableDiff,
    type TableGridView as TableGridViewType,
    table,
} from '@gitbook/doc-core';
import type { TableSelection } from '@gitbook/doc-core/src/table/selection';
import type { Block } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import type { VirtualizationItemInput } from '@gitbook/util-react-virtual';
import { useVirtualizedList } from '@gitbook/util-react-virtual';

import { useDocumentNodeComments } from '../../../../Commenting';
import { DEFAULT_TABLE_COLUMN_WIDTH, getEstimatedTableRowHeight } from '../../../common';
import { ContentEditorContext } from '../../../contexts';
import { DefinitionOptionsButton } from '../../ColumnDefinitions/buttons/DefinitionOptionsButton';
import { DefinitionTitleView } from '../../ColumnDefinitions/DefinitionTitleView';
import { useComputeTableDefinitionColorKinds } from '../../ColumnDefinitions/hooks';
import { TableStatesContext } from '../../contexts';
import { useTableLayout } from '../../hooks/useTableLayout';
import type { TableRecordKV } from '../../types';
import {
    ColumnWidthsContext,
    type ColumnWidthsMap,
    VerticalAlignmentContext,
} from '../../utils/contexts';
import type { TableViewProps } from '../types';
import { computeColumnHeights } from '../utils';
import { handleColumnReorder, handleRowReorder } from '../utils/reordering';
import { ColumnDragHandle } from './buttons/ColumnDragHandle';
import { RowSettings, RowSettingsObfuscator } from './buttons/RowSettings';
import { ColumnDragPreview } from './ColumnDragPreview';
import { ColumnResizeHandle } from './ColumnResizeHandle';
import { useTableRowOptions } from './hooks/useTableRowOptions';
import { TableRecordValue } from './TableRecordValue';
import { forgivingHeaderColumnsCollisions } from './utils/dndkit/forgivingHeaderColumnsCollisions';
import { createSoftClampModifier } from './utils/dndkit/rubberBandModifier';
import { type ColumnDragState, getColumnReorderState } from './utils/getColumnReorderStateProps';
import { useColumnOffset } from './utils/useColumnOffset';

/**
 * How many rows should the table contain before we start virtualizing.
 * We choose 20 here as this is the point where a table may reasonably start to overflow
 * outside the viewport on the smaller desktop screens.
 */
const ROWS_BEFORE_VIRTUALIZE = 20;
// The max distance we allow a column to be dragged away from the table
const MAX_DRAG_DISTANCE = 80;

const dropAnimation = {
    duration: 300,
    easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
};

/**
 * Renderer for a table as a grid.
 */
export function TableGridView(props: TableViewProps<TableGridViewType>): React.ReactElement {
    const { view } = props;

    // Stateful ref for virtualization as the TableGridViewInner's virtualization should update when current property on the ref gets defined (it uses it internally to define an intersection observer on that parent).
    const [parentRef, setParentRef] = React.useState<Element>();
    const parentRefCallback = React.useCallback((node) => {
        if (node !== null) {
            setParentRef(node);
        }
    }, []);

    // Retrieve the calculated widths for the provider
    const { widths } = useTableLayout(view);

    return (
        <div ref={parentRefCallback} className="-translate-x-2.5 relative w-fit max-w-full">
            <ColumnWidthsContext.Provider value={widths}>
                <TableGridViewInner {...props} parentRef={parentRef} />
            </ColumnWidthsContext.Provider>
        </div>
    );
}

/**
 * Inner component for rendering the view. Gives us access to the ColumnsWidthContext.
 */
function TableGridViewInner(
    props: TableViewProps<TableGridViewType> & {
        parentRef: Element | undefined;
    }
): React.ReactElement {
    const {
        selection,
        records,
        definition,
        view,
        readOnly,
        editor,
        nodeKey: tableKey,
        openRecord,
        parentRef,
        isTableBlockFocused,
        diff,
    } = props;
    const tableNode = editor.value.document.getNode(tableKey) as Block | undefined;
    const widths = React.useContext(ColumnWidthsContext);
    const rowHeightsRef = React.useRef<Record<string, number>>({});
    const columnHeightsRef = React.useRef<Record<string, number>>({});
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);

    const [isTooFar, setIsTooFar] = React.useState(false);
    const [isReorderingRows, setIsReorderingRows] = React.useState(false);
    const [columnDragState, setColumnDragState] = React.useState<ColumnDragState>({
        activeColumnId: null,
        columnIsOverColumn: null,
        activeColumnIndex: null,
        columnOverIndex: null,
        dragDistance: 0,
    });

    const isColumnReordering = !!columnDragState.activeColumnId;
    const activeColumnId = columnDragState.activeColumnId;

    const headerCanBeHidden =
        view.hideHeader || (readOnly && table.isHeaderEmpty({ definition, view, records: {} }));
    const withHeader =
        !!diff || // We always want to show the header in diff mode
        !headerCanBeHidden ||
        !!selection.recordId ||
        isTableBlockFocused ||
        isColumnReordering;

    const recordIds = React.useMemo(() => records.map((r) => r.key), [records]);

    const virtualizedRows = useVirtualizedList({
        enabled: records.length > ROWS_BEFORE_VIRTUALIZE,
        parentRef: parentRef ? { current: parentRef } : undefined,
        getEstimatedItemHeight: (item) => {
            return getEstimatedTableRowHeight(tableNode, String(item.key), widths);
        },
        list: records.map<VirtualizationItemInput>((record) => ({
            key: record.key,
        })),
    });

    // Only register dragging if some distance has travelled, otherwise we want to detect a palette click, not drag.
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const measureRowHeight = React.useCallback((recordKey: string) => {
        return (el: HTMLTableRowElement | null) => {
            if (el) {
                rowHeightsRef.current[recordKey] = el.offsetHeight;
            }
        };
    }, []);

    // (re)compute column heights when records or view changes.
    React.useEffect(() => {
        computeColumnHeights(view, records, rowHeightsRef, columnHeightsRef);
    }, [records, view]);

    const isOverOrigin =
        columnDragState.activeColumnId &&
        columnDragState.activeColumnId === columnDragState.columnIsOverColumn;

    return (
        <Table
            isReorderingRows={isReorderingRows}
            view={view}
            isColumnReordering={isColumnReordering}
        >
            <DndContext
                onDragOver={({ collisions }) => {
                    const isNoCollisions = !collisions || collisions.length === 0;
                    setIsTooFar(isNoCollisions);
                }}
                onDragStart={(event) => {
                    setColumnDragState({
                        activeColumnId: event.active.id as string,
                        columnIsOverColumn: null,
                        activeColumnIndex: view.columns.indexOf(event.active.id as string),
                        columnOverIndex: null,
                        dragDistance: 0,
                    });
                }}
                onDragMove={(event: DragMoveEvent) => {
                    // See getColumnReorderState for more details why we do this and not the dnd context.
                    setColumnDragState({
                        activeColumnId: event.active.id as string,
                        columnIsOverColumn: event.over?.id as string,
                        activeColumnIndex: view.columns.indexOf(event.active.id as string),
                        columnOverIndex: view.columns.indexOf(event.over?.id as string),
                        dragDistance: event.delta.x,
                    });
                }}
                onDragCancel={() => {
                    setColumnDragState({
                        activeColumnId: null,
                        columnIsOverColumn: null,
                        activeColumnIndex: null,
                        columnOverIndex: null,
                        dragDistance: 0,
                    });
                    setIsTooFar(false);
                }}
                onDragEnd={(event: DragEndEvent) => {
                    setColumnDragState({
                        activeColumnId: null,
                        columnIsOverColumn: null,
                        activeColumnIndex: null,
                        columnOverIndex: null,
                        dragDistance: 0,
                    });
                    handleColumnReorder({
                        event,
                        editor,
                        tableKey,
                        recordId: records[view.columns.indexOf(String(event.active.id))].key,
                    });
                    setIsTooFar(false);
                }}
                collisionDetection={forgivingHeaderColumnsCollisions(view.columns)}
                modifiers={
                    parentRef
                        ? [
                              createSoftClampModifier({
                                  boundingRect: parentRef.getBoundingClientRect(),
                                  threshold: MAX_DRAG_DISTANCE,
                                  friction: 0.3,
                              }),
                          ]
                        : []
                }
            >
                {createPortal(
                    <DragOverlay dropAnimation={dropAnimation}>
                        {activeColumnId ? (
                            <div className="relative">
                                <ColumnDragPreview
                                    columnId={activeColumnId}
                                    records={records}
                                    definition={definition}
                                    view={view}
                                    editor={editor}
                                    tableKey={tableKey}
                                    width={widths.get(activeColumnId) ?? DEFAULT_TABLE_COLUMN_WIDTH}
                                    rowHeights={rowHeightsRef.current}
                                    isTooFar={isTooFar}
                                />
                                {/* Our Tooltip doesn't work well when used within a portal, so unfortunately for now we just show a simple tooltip. */}
                                {isTooFar || isOverOrigin ? (
                                    <div className="-top-10 -translate-x-1/2 absolute left-1/2 z-[--z-index-toast] flex items-center justify-center whitespace-nowrap rounded bg-tile-inverted px-2 py-1 text-inverted text-ui-base shadow-md">
                                        <span>
                                            {isOverOrigin ? 'Keep position' : 'Release to cancel'}
                                        </span>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </DragOverlay>,
                    document.body
                )}
                {withHeader ? (
                    <SortableContext items={view.columns} strategy={horizontalListSortingStrategy}>
                        <TableHeader>
                            <RowSettingsObfuscator />
                            {view.columns.map((colId, index) => {
                                const isLastColumn = index === view.columns.length - 1;
                                const columnDef = definition[colId];

                                return (
                                    <TableColumn
                                        key={colId}
                                        id={colId}
                                        className={clsx(isLastColumn && 'border-r')}
                                        width={widths.get(colId) ?? DEFAULT_TABLE_COLUMN_WIDTH}
                                        tableKey={tableKey}
                                        editor={editor}
                                        columnOrder={view.columns}
                                        isColumnReordering={isColumnReordering}
                                        columnDragState={columnDragState}
                                        view={view}
                                        columnDef={columnDef}
                                        isTooFar={isTooFar}
                                        diff={diff}
                                    />
                                );
                            })}
                        </TableHeader>
                    </SortableContext>
                ) : null}

                {/* Enables row reordering, only wraps the table body as column-reordering is for now still handled by our global DndContext */}
                <DndContext
                    onDragStart={() => setIsReorderingRows(true)}
                    onDragCancel={() => setIsReorderingRows(false)}
                    onDragEnd={(event: DragEndEvent) => {
                        setIsReorderingRows(false);
                        handleRowReorder({
                            event,
                            editor,
                            tableKey,
                            records,
                            definitionId: view.columns[0],
                        });

                        logAnalyticsEvent({
                            type: 'edit_page_document_move_block',
                            method: 'dnd',
                            blockType: 'table-row',
                            toParent: 'table',
                            fromParent: 'table',
                        });
                    }}
                    sensors={sensors}
                    modifiers={[restrictToVerticalAxis]}
                >
                    <SortableContext items={recordIds} strategy={verticalListSortingStrategy}>
                        <TableBody isColumnReordering={isColumnReordering}>
                            {virtualizedRows.map(([node, visible, height], index) => {
                                const record = records[index];
                                const beforeRecord = index > 0 ? records[index - 1] : undefined;
                                const afterRecord = records[index + 1];

                                return visible ? (
                                    <TableRow
                                        ref={node.nodeRef}
                                        key={record.key}
                                        id={record.key}
                                        tableKey={tableKey}
                                        editor={editor}
                                        openRecord={openRecord}
                                        view={view}
                                        record={record}
                                        records={records}
                                        beforeOrderIndex={beforeRecord?.value.orderIndex}
                                        afterOrderIndex={afterRecord?.value.orderIndex}
                                        isLastRow={index === records.length - 1}
                                        isFirstRow={index === 0}
                                        definition={definition}
                                        selection={selection}
                                        widths={widths}
                                        withHeader={withHeader}
                                        isColumnReordering={isColumnReordering}
                                        columnDragState={columnDragState}
                                        measureRowHeight={measureRowHeight}
                                        isTooFar={isTooFar}
                                        diff={diff}
                                    />
                                ) : (
                                    // Virtualized table row with plain text (for search) and estimated height.
                                    <TextOnlyTableRow
                                        ref={node.nodeRef}
                                        key={record.key}
                                        height={height}
                                        columns={view.columns}
                                        columnWidths={widths}
                                        values={table.getTableRowPlainTexts(
                                            tableNode,
                                            record.value
                                        )}
                                        data-virtual
                                    />
                                );
                            })}
                        </TableBody>
                    </SortableContext>
                </DndContext>
            </DndContext>
        </Table>
    );
}

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
    isReorderingRows: boolean;
    view: TableGridViewType;
    isColumnReordering: boolean;
}

/**
 * Top-level Table component that wraps the entire "table" structure
 */
function Table({ children, isReorderingRows, view }: TableProps) {
    const { tableIsScrollable } = useTableLayout(view);
    return (
        <table
            className={clsx(
                'scrollbar group/table relative flex w-fit max-w-full flex-col overflow-y-hidden pt-2 **:data-drop-target:border **:data-drop-target:border-primary',
                // Prevent tables from scrolling during drag events, which browsers can interpret as scrolling the table.
                isReorderingRows ? 'overflow-x-hidden' : 'overflow-x-auto',
                tableIsScrollable ? 'pb-0.5' : 'pb-0'
            )}
        >
            {children}
        </table>
    );
}

/**
 * TableHeader
 */
interface TableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
    className?: string;
}

function TableHeader({ children }: TableHeaderProps) {
    return <thead className="flex w-full select-all flex-row font-bold">{children}</thead>;
}

/**
 * TableBody
 */
interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
    isColumnReordering: boolean;
}

function TableBody({ children, isColumnReordering }: TableBodyProps) {
    return (
        <tbody className={clsx('flex flex-col', isColumnReordering && 'pointer-events-none')}>
            {children}
        </tbody>
    );
}

/**
 * TableRow
 */
interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    className?: string;
    /** Key of the parent Table block */
    tableKey: string;
    /** Editor instance */
    editor: EditorContainer;
    /** Callback to expand the record as a modal */
    openRecord: (recordId: string) => void;
    /** View configured in the table */
    view: TableGridViewType;
    /** Record to render in this row */
    record: TableRecordKV;
    /** Index of the record before this one */
    beforeOrderIndex?: string;
    /** Index of the record after this one */
    afterOrderIndex?: string;
    /** Whether this is the first row */
    isFirstRow?: boolean;
    /** Whether this is the last row */
    isLastRow?: boolean;
    /** All records in the table */
    records: TableRecordKV[];
    /** Table definition */
    definition: TableDefinitions;
    /** Selection in the table */
    selection: TableSelection;
    /** Widths */
    widths: ColumnWidthsMap;
    /** Whether the table has a header */
    withHeader: boolean;
    /** Whether column reordering is active */
    isColumnReordering: boolean;
    /** Column drag state */
    columnDragState: ColumnDragState;
    /** Measure row height */
    measureRowHeight: (recordKey: string) => (el: HTMLTableRowElement | null) => void;
    /** Whether the dragged column is too far */
    isTooFar: boolean;
    /** Computed diff of the table block */
    diff?: TableDiff;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>((props, ref) => {
    const {
        tableKey,
        editor,
        view,
        record,
        beforeOrderIndex,
        afterOrderIndex,
        isLastRow,
        definition,
        selection,
        widths,
        withHeader,
        openRecord,
        isColumnReordering,
        columnDragState,
        isTooFar,
        measureRowHeight,
        diff,
    } = props;
    const definitionColorKinds = useComputeTableDefinitionColorKinds(definition, view.columns);
    const tableStates = React.useContext(TableStatesContext);
    const tableState = tableStates.get(tableKey);

    // Use the table row options hook
    const {
        paletteHandle: rowOptionsPaletteHandle,
        paletteButtonRef: rowOptionsButtonRef,
        onPressOptions,
    } = useTableRowOptions({
        record,
        beforeOrderIndex,
        afterOrderIndex,
        isLastRow,
        editor,
        tableKey,
        openRecord,
        selection,
        tableState,
    });
    const {
        activeIndex,
        index,
        newIndex,
        overIndex,
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
        isSorting,
    } = useSortable({
        id: record.key,
    });

    const rowRefs = React.useCallback(
        (el: HTMLTableRowElement | null) => {
            // Attach your original DnD kit's nodeRef:
            setNodeRef(el);

            // Also store the row height:
            measureRowHeight(record.key)(el);

            // If you're also forwarding the "external" ref, do that here:
            if (typeof ref === 'function') ref(el);
            else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = el;
        },
        [record.key, setNodeRef, ref, measureRowHeight]
    );

    const style: React.CSSProperties = {
        // Move the row in sync with drag
        transform: isColumnReordering ? undefined : CSS.Translate.toString(transform),
        transition,
    };

    return (
        <tr
            className={clsx(
                'group/tablerow relative flex w-full cursor-default flex-row overflow-visible bg-base transition-colors',
                isDragging && 'z-10 cursor-grabbing',
                isSorting && !isDragging && 'pointer-events-none',
                props.className
            )}
            ref={rowRefs}
            style={style}
        >
            <RowSettings
                editor={editor}
                tableKey={tableKey}
                record={record}
                attributes={attributes}
                listeners={listeners}
                isDragging={isDragging}
                paletteHandle={rowOptionsPaletteHandle}
                paletteButtonRef={rowOptionsButtonRef}
                onPressOptions={onPressOptions}
            />
            {view.columns.map((columnId, columnIndex) => {
                const columnDef = definition[columnId];
                const selected =
                    selection.recordId === record.key && selection.definitionId === columnId;

                /* Vertical alignment is configurable on column-level */
                const verticalAlignment =
                    columnDef.type === 'text' && columnDef.verticalAlignment
                        ? columnDef.verticalAlignment
                        : VerticalAlignment.Middle;

                const isLastColumn = columnIndex === view.columns.length - 1;
                const isFirstColumn = columnIndex === 0;

                // Our table setup requires to use borders on the cells instead of rows. As rows are not static since they can be dragged,
                // the ommitted border-top should be shown depending on the drag-states of each row. On top of that, we should also factor in header-visibility.

                // This leads to us showing border-top when the current mapped row:
                // - is dragged, to display the blue outline
                // - is either the static or dragged *first* row, only if the table has header-visiblity off.
                const noHeaderAndFirstRow = newIndex === 0 && !withHeader;

                // - its row above is only dragged, but not yet snapped into a new position
                const rowAboveIsLifted = activeIndex === index - 1;
                const liftedRowHasntRepositioned = overIndex === activeIndex && activeIndex !== -1;
                const rowAboveIsLiftedButNotSnapped =
                    rowAboveIsLifted && liftedRowHasntRepositioned;

                // - the dragged row is positioned above the current row, but was not the lifted one
                const liftedRowIsOverTheAboveRow = overIndex === newIndex - 1;
                const aboveRowIsTheDraggedRowsOverPosition =
                    !rowAboveIsLifted && liftedRowIsOverTheAboveRow;

                const showBorderTop =
                    isDragging ||
                    noHeaderAndFirstRow ||
                    rowAboveIsLiftedButNotSnapped ||
                    aboveRowIsTheDraggedRowsOverPosition;

                const {
                    isInDraggedColumn,
                    isOverMe,
                    insertPosition,
                    showBorderRight,
                    showBorderLeft,
                } = getColumnReorderState({
                    columnId,
                    columnIndex,
                    columnDragState,
                });

                const opacity = getDraggedColumnOpacity(
                    isInDraggedColumn,
                    columnDragState.dragDistance,
                    isTooFar
                );

                const cellId = `${tableKey}-row-${record.key}-col-${columnId}`;

                return (
                    <VerticalAlignmentContext.Provider value={verticalAlignment} key={columnId}>
                        <TableColumnInsertionSpacer
                            show={isOverMe && insertPosition === Position.Before}
                        />
                        <TableCell
                            tableKey={tableKey}
                            recordId={record.key}
                            columnId={columnId}
                            opacity={opacity}
                            className={clsx(
                                'border-control border-b',
                                isLastColumn && 'border-r',
                                showBorderTop && 'border-t',
                                isDragging &&
                                    'pointer-events-none border-[var(--sp-color-border-table-active-inner)] border-y-[var(--sp-color-border-table-active-outer)] bg-action-active',
                                isDragging &&
                                    isFirstColumn &&
                                    'border-l-[var(--sp-color-border-table-active-outer)]',
                                isDragging &&
                                    isLastColumn &&
                                    'border-r-[var(--sp-color-border-table-active-outer)]',
                                showBorderLeft && 'border-l',
                                showBorderRight && 'border-r'
                            )}
                            width={widths.get(columnId)}
                            key={`${record.key}-${columnId}`}
                            onMouseDown={(event) => {
                                // If the event target is not the cell itself,
                                // we return so we use the default behavior (e.g. text selection, select checkbox, etc).
                                if (event.target !== event.currentTarget) {
                                    return;
                                }

                                // otherwise select the table cell
                                event.stopPropagation();
                                event.preventDefault();
                                editor.change((change) => {
                                    table.moveTableSelection(change, tableKey, {
                                        recordId: record.key,
                                        definitionId: columnId,
                                    });
                                });
                            }}
                            selected={selected}
                            readOnly={editor.readOnly}
                        >
                            <TableRecordValue
                                definitionColorKind={definitionColorKinds[columnId]}
                                record={record}
                                selected={selected}
                                columnDefinition={columnDef}
                                nodeKey={tableKey}
                                editor={editor}
                                readOnly={editor.readOnly}
                                view={view}
                                diff={diff}
                                onPressRowOptions={
                                    selection.recordId === record.key ? onPressOptions : undefined
                                }
                                cellId={cellId}
                            />
                        </TableCell>
                        <TableColumnInsertionSpacer
                            show={isOverMe && insertPosition === Position.After}
                        />
                    </VerticalAlignmentContext.Provider>
                );
            })}
        </tr>
    );
});

/**
 * A simplified row rendered as fallback when a row is not visible yet in virtualization.
 * We render text-only values to ensure searching for the text still works, but no other data or styles.
 */
const TextOnlyTableRow = React.forwardRef(
    (
        {
            columns,
            columnWidths,
            values,
            height,
        }: {
            columns: string[];
            columnWidths: ColumnWidthsMap;
            values: string[];
            height: number;
        },
        ref: React.Ref<HTMLTableRowElement>
    ) => {
        return (
            <tr
                className="ml-2.5 flex w-fit items-center border-control border-x border-b"
                style={{ maxHeight: height, height }}
                ref={ref}
            >
                {columns.map((colId, index) => {
                    const width = columnWidths.get(colId) ?? DEFAULT_TABLE_COLUMN_WIDTH;
                    return (
                        <div
                            key={colId}
                            className={clsx(
                                'flex h-full items-center p-3 text-content-paragraph',
                                index !== columns.length - 1 && 'border-r'
                            )}
                            style={{
                                width,
                                minWidth: width,
                            }}
                        >
                            {values[index]}
                        </div>
                    );
                })}
            </tr>
        );
    }
);

export enum Position {
    Before = -1,
    After = 1,
}

/**
 * TableColumn
 */
interface TableColumnProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string;
    id: string;
    width: number;
    tableKey: string;
    editor: EditorContainer;
    columnOrder: string[];
    isColumnReordering: boolean;
    columnDragState: ColumnDragState;
    view: TableGridViewType;
    columnDef: TableColumnDefinition;
    isTooFar: boolean;
    diff?: TableDiff;
}

function TableColumn(props: TableColumnProps) {
    const { id, columnOrder, columnDragState } = props;
    const culumativeOffset = useColumnOffset({ columnId: id, columnOrder });
    const { isOverMe, insertPosition, showBorderRight, showBorderLeft } = getColumnReorderState({
        columnId: id,
        columnIndex: columnOrder.indexOf(id),
        columnDragState,
    });

    return (
        <>
            <TableColumnInsertionSpacer show={isOverMe && insertPosition === Position.Before} />
            <TableColumnHeader
                cumulativeOffset={culumativeOffset}
                insertPosition={insertPosition}
                isOverMe={isOverMe}
                showBorderLeft={showBorderLeft}
                showBorderRight={showBorderRight}
                {...props}
            />
            <TableColumnInsertionSpacer show={isOverMe && insertPosition === Position.After} />
        </>
    );
}

function TableColumnHeader(
    props: TableColumnProps & {
        insertPosition: Position | undefined;
        cumulativeOffset: number;
        isOverMe: boolean;
        showBorderLeft: boolean;
        showBorderRight: boolean;
    }
): React.ReactElement {
    const {
        className,
        width,
        id,
        tableKey,
        editor,
        columnOrder,
        isColumnReordering,
        columnDragState,
        view,
        columnDef,
        isTooFar,
        cumulativeOffset,
        isOverMe,
        insertPosition,
        showBorderLeft,
        showBorderRight,
        diff,
    } = props;

    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id,
    });

    const droppedLineIndicatorPosition =
        cumulativeOffset + 18 + (insertPosition === Position.After ? width : 0);

    const isEditMode = !editor.readOnly;
    const opacity = getDraggedColumnOpacity(isDragging, columnDragState.dragDistance, isTooFar);

    // We are in diff view if the header visibility is changed or there is a change in the column definition.
    const diffViewHeaderBackgroundColor =
        typeof diff?.view.hideHeader === 'undefined'
            ? '--sp-color-bg-muted'
            : diff.view.hideHeader.after
              ? '--sp-color-bg-danger-default'
              : '--sp-color-bg-success-default';
    const diffViewColumnDefinition = diff?.definitions.get(id);

    return (
        <th
            ref={setNodeRef}
            scope="col"
            className={clsx(
                'group/tablecolumn flex shrink-0 items-center justify-center border-y px-3 py-3',
                className,
                isColumnReordering && 'pointer-events-none',
                showBorderRight && 'border-r',
                showBorderLeft && 'border-l'
            )}
            style={{
                borderColor: `color-mix(in srgb, var(--sp-color-border-control) ${opacity * 100}%, transparent)`,
                // Background color is using the diff view (if applicable)
                backgroundColor: `color-mix(in srgb, var(${diffViewHeaderBackgroundColor}) ${opacity * 100}%, transparent)`,
                width,
                maxWidth: width,
            }}
            {...attributes}
            tabIndex={-1}
        >
            <div
                className="flex-1 select-all truncate whitespace-nowrap text-left font-medium"
                style={{ opacity }}
            >
                {typeof diffViewColumnDefinition === 'undefined' ? (
                    <DefinitionTitleView
                        editor={editor}
                        nodeKey={tableKey}
                        columnDef={columnDef}
                        readOnly={!isEditMode}
                    />
                ) : (
                    (() => {
                        const { title, ...afterColumnDefinitionKeysWithoutTitle } =
                            diffViewColumnDefinition.after;

                        // Check if only the title has changed, and all other keys are the same.
                        const onlyTitleChanged =
                            diffViewColumnDefinition.before?.title !==
                                diffViewColumnDefinition.after.title &&
                            Object.keys(afterColumnDefinitionKeysWithoutTitle).every(
                                (key) =>
                                    diffViewColumnDefinition.after[key] ===
                                    diffViewColumnDefinition.before?.[key]
                            );

                        // If only the title has changed, we render the title view with diff.
                        if (onlyTitleChanged) {
                            return (
                                <DefinitionTitleView
                                    editor={editor}
                                    nodeKey={tableKey}
                                    columnDef={columnDef}
                                    readOnly={!isEditMode}
                                    diff={{
                                        before: diffViewColumnDefinition.before?.title ?? '',
                                        after: diffViewColumnDefinition.after.title,
                                    }}
                                />
                            );
                        }

                        // Otherwise, we render both the before and after definitions.
                        return (
                            <div className="flex flex-col gap-1">
                                {diffViewColumnDefinition.before ? (
                                    <div className="bg-danger-standout p-1">
                                        <DefinitionTitleView
                                            editor={editor}
                                            nodeKey={tableKey}
                                            columnDef={diffViewColumnDefinition.before}
                                            readOnly={!isEditMode}
                                        />
                                    </div>
                                ) : null}
                                <div className="bg-success-standout p-1">
                                    <DefinitionTitleView
                                        editor={editor}
                                        nodeKey={tableKey}
                                        columnDef={diffViewColumnDefinition.after}
                                        readOnly={!isEditMode}
                                    />
                                </div>
                            </div>
                        );
                    })()
                )}
            </div>

            {isEditMode ? (
                <ColumnResizeHandle
                    columnId={id}
                    tableKey={tableKey}
                    editor={editor}
                    initialWidth={width}
                    columnOrder={columnOrder}
                />
            ) : null}

            {isEditMode ? (
                <ColumnDragHandle
                    listeners={listeners}
                    isDragging={isDragging}
                    columnWidth={width}
                    columnId={id}
                    columnOrder={columnOrder}
                />
            ) : null}

            {isEditMode && !isColumnReordering ? (
                <DefinitionOptionsButton
                    editor={editor}
                    nodeKey={tableKey}
                    leadingIcon={Icon.MoreVertical}
                    view={view}
                    columnDef={columnDef}
                />
            ) : null}

            <DropIndicator position={droppedLineIndicatorPosition} isOverMe={isOverMe} />
        </th>
    );
}

/**
 * TableCell
 */
interface TableCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
    className?: string;
    width?: number;
    selected?: boolean;
    readOnly: boolean;
    opacity?: number;
    tableKey: string;
    recordId: string;
    columnId: string;
}

function TableCell({
    children,
    className,
    width,
    onMouseDown,
    selected,
    readOnly,
    opacity,
    recordId,
    columnId,
    tableKey,
    ...props
}: TableCellProps) {
    const verticalAlignment = React.useContext(VerticalAlignmentContext);
    const { nodeState } = useDocumentNodeComments({
        nodeKey: tableKey,
        tableCell: { record: recordId, definition: columnId },
    });

    return (
        <td
            className={clsx(
                'group/tablecell -outline-offset-1 flex min-h-12 overflow-hidden whitespace-nowrap px-3 py-3 outline outline-1',
                selected
                    ? 'outline-[var(--sp-color-border-table-active-outer)]'
                    : 'outline-transparent',
                nodeState.isOpened &&
                    'shadow-[inset_0_-2px_0_0_var(--sp-color-comments-block-border)]',
                nodeState.isHovered || nodeState.isOpened
                    ? 'bg-[var(--sp-color-comments-block-bg)]'
                    : selected
                      ? 'bg-[var(--sp-color-bg-table-cell-active)]'
                      : null,
                verticalAlignment === 'middle' && 'items-center',
                verticalAlignment === 'top' && 'items-start',
                verticalAlignment === 'bottom' && 'items-end',
                !readOnly && 'cursor-pointer',
                className
            )}
            style={{
                width,
                maxWidth: width,
                minWidth: width,
                opacity,
            }}
            onMouseDown={onMouseDown}
            {...props}
        >
            {children}
        </td>
    );
}

/**
 * Horizontal spacer that appears to the left or right of the column header & each cell in this column
 */
function TableColumnInsertionSpacer({ show }: { show: boolean }) {
    return (
        <div
            data-exclude-from-copy
            className={clsx(
                'flex h-full w-0 flex-1 items-center justify-center bg-base transition-all',
                show && 'w-5 min-w-5'
            )}
        />
    );
}

/**
 * Drop indicator that shows where a column will be inserted when dragging
 */
function DropIndicator({ position, isOverMe }: { position: number; isOverMe: boolean }) {
    return (
        <div
            className={clsx(
                '-bottom-[-1px] absolute top-1.5 z-50 flex justify-center pl-0.5 opacity-0 transition-opacity duration-200',
                isOverMe && 'opacity-100'
            )}
            style={{ left: position }}
        >
            <div
                className={clsx(
                    'relative w-[1.5px] bg-[var(--sp-color-bg-table-drag-button-active)]',
                    'before:-translate-x-1/2 before:absolute before:top-0 before:left-1/2 before:box-content before:h-1.5 before:w-1.5 before:rounded-full before:border-[1.5px] before:border-[var(--sp-color-bg-table-drag-button-active)] before:border-solid before:bg-base before:content-[""]',
                    'after:-translate-x-1/2 after:absolute after:bottom-0 after:left-1/2 after:box-content after:h-1.5 after:w-1.5 after:rounded-full after:border-[1.5px] after:border-[var(--sp-color-bg-table-drag-button-active)] after:border-solid after:bg-base after:content-[""]'
                )}
            />
        </div>
    );
}

/**
 * Helper to transform the distance (0 to 50/-50) of a dragged column into an opacity value between 0 and 1.
 *
 * Used to fade in/out the dragged column's original place as it's dragged closer/further away.
 */
const getDraggedColumnOpacity = (isDragging: boolean, dragDistance: number, isTooFar: boolean) => {
    if (!isDragging || isTooFar) return 1;
    return Math.max(Math.min(1 - Math.abs(dragDistance) / 50, 1), 0);
};
